/**
 * Text to Binary Tool
 *
 * Convert text to binary representation using browser-native APIs.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const TextToBinaryTool: ITransformerTool = {
  id: 'text-to-binary',
  name: 'Text → Binary',
  description: 'Convert text to binary representation',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['text', 'binary', 'convert', 'encode', 'bits'],

  seo: {
    slug: 'text-to-binary',
    pageTitle: 'Text to Binary Converter - Free Online Tool',
    metaDescription: 'Convert text to binary representation with configurable separator and bit width. Supports 7-bit ASCII and 8-bit UTF-8. Runs entirely in your browser.',
    keywords: ['text to binary', 'binary converter', 'ascii to binary', 'text to bits', 'binary encoder'],
    relatedTools: ['binary-to-text', 'hex-encode', 'base64-encode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'separator',
      type: 'select',
      label: 'Separator',
      defaultValue: 'space',
      options: ['space', 'none'],
      description: 'Separator between binary bytes',
    },
    {
      name: 'bitWidth',
      type: 'select',
      label: 'Bit Width',
      defaultValue: '8',
      options: ['7', '8'],
      description: 'Bits per character (7 = ASCII, 8 = full byte)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '');
    if (!text) return '';

    const separator = String(config.separator ?? 'space') === 'space' ? ' ' : '';
    const bitWidth = Number(config.bitWidth ?? 8);

    const encoder = new TextEncoder();
    const bytes = encoder.encode(text);

    const binaryStrings = Array.from(bytes).map(byte =>
      byte.toString(2).padStart(bitWidth, '0')
    );

    return binaryStrings.join(separator);
  },
};
